const CozyologyPredictiveSearch = (() => {
  const normalize = (value) => String(value || '').normalize('NFKD').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
  const words = (value) => normalize(value).split(' ').filter(Boolean);
  const matchesWordPrefixes = (query, candidate) => {
    const queryWords = words(query);
    const candidateWords = words(candidate);
    return queryWords.length > 0 && queryWords.every((queryWord) => candidateWords.some((candidateWord) => candidateWord.startsWith(queryWord)));
  };
  const isSwatchIntent = (query) => words(query).some((word) => 'swatch'.startsWith(word) || 'sample'.startsWith(word) || 'booklet'.startsWith(word));
  return { normalize, words, matchesWordPrefixes, isSwatchIntent };
})();

if (typeof window !== 'undefined') window.CozyologyPredictiveSearch = CozyologyPredictiveSearch;

const MainSearch = class extends HTMLElement {
  constructor() {
    super();
    this.lastTrigger = null;
    this.requestSequence = 0;

    if (typeof document.addEventListener === 'function') {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest && event.target.closest('.show-search-link');
        if (trigger) this.lastTrigger = trigger;
      }, true);
    }
    this.querySelectorAll('.main-search__close').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeSearch();
      });
    });
    const input = this.querySelector('.main-search__input');
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSearch();
      }
    });
    if (this.dataset.quickSearch === 'true') this.initQuickSearch();
  }

  closeSearch() {
    document.body.classList.remove('show-search');
    const input = this.querySelector('.main-search__input');
    if (typeof input.setAttribute === 'function') input.setAttribute('aria-expanded', 'false');
    if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') this.lastTrigger.focus();
  }

  initQuickSearch() {
    const searchInput = this.querySelector('.main-search__input');
    const resultsBox = this.querySelector('.main-search__results');
    const searchTimeoutThrottle = 300;
    const resultLimit = 10;
    const groupLimit = 4;
    const includeMeta = this.dataset.quickSearchMeta === 'true';
    let searchTimeoutID = -1;
    let searchAbortControllers = [];
    const setExpanded = (value) => {
      if (typeof searchInput.setAttribute === 'function') searchInput.setAttribute('aria-expanded', value);
    };
    const setBusy = (value) => {
      if (typeof resultsBox.setAttribute === 'function') resultsBox.setAttribute('aria-busy', value);
    };

    const abortSearch = () => {
      searchAbortControllers.forEach((controller) => controller.abort());
      searchAbortControllers = [];
      clearTimeout(searchTimeoutID);
    };
    const buildLinkURL = (headerStyleQuery) => {
      const form = searchInput.closest('form');
      const linkURL = new URL(form.action);
      const formParams = new URLSearchParams(new FormData(form));
      formParams.forEach((value, key) => { linkURL.searchParams.set(key, value); });
      if (headerStyleQuery) {
        linkURL.searchParams.set('q', headerStyleQuery);
        linkURL.searchParams.set('type', 'product');
      }
      return linkURL;
    };
    const buildAjaxURL = (query, headerStyleQuery, linkURL) => {
      if (!theme.Shopify.features.predictiveSearch) {
        const fallbackURL = new URL(linkURL);
        fallbackURL.searchParams.set('q', query);
        fallbackURL.searchParams.set('section_id', 'main-search');
        return fallbackURL;
      }
      const ajaxURL = new URL(theme.routes.predictiveSearch, window.location?.origin || theme.routes.base);
      ajaxURL.searchParams.set('q', query);
      ajaxURL.searchParams.set('section_id', 'predictive-search');
      ajaxURL.searchParams.set('resources[limit]', resultLimit);
      ajaxURL.searchParams.set('resources[options][fields]', includeMeta ? 'title,product_type,variants.title,vendor,tag,variants.sku' : 'title,product_type,variants.title,vendor,tag');
      if (window.location?.hostname?.endsWith('.myshopify.com')) ajaxURL.searchParams.set('_fd', '0');
      if (headerStyleQuery) {
        ajaxURL.searchParams.set('resources[type]', 'product');
        ajaxURL.searchParams.set('resources[options][fields]', 'tag');
      }
      return ajaxURL;
    };
    const requestText = (url, controller) => {
      if (typeof XMLHttpRequest === 'undefined') {
        return fetch(url, { method: 'get', signal: controller.signal }).then((response) => {
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          return response.text();
        });
      }
      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('GET', url.toString(), true);
        request.onload = () => {
          if (request.status >= 200 && request.status < 300) resolve(request.responseText);
          else reject(new Error(`HTTP error! Status: ${request.status}`));
        };
        request.onerror = () => reject(new Error('Search request failed'));
        request.onabort = () => {
          const error = new Error('Search request aborted');
          error.name = 'AbortError';
          reject(error);
        };
        controller.signal.addEventListener('abort', () => request.abort(), { once: true });
        request.send();
      });
    };
    const parseResponse = (response) => {
      const template = document.createElement('template');
      template.innerHTML = response;
      return theme.Shopify.features.predictiveSearch ? template.content.querySelector('.product-grid') : template.content.querySelector('.section-search-template .product-grid');
    };
    const prepareProductBlock = (block) => {
      block.classList.add('main-search-result');
      block.querySelectorAll('.btn.quickbuy-toggle').forEach((button) => button.remove());
      block.querySelectorAll('.quickbuy-toggle').forEach((element) => element.classList.remove('quickbuy-toggle'));
      return block;
    };
    const makeGroup = (title, modifier, blocks) => {
      const group = document.createElement('section');
      group.className = `main-search__result-group main-search__result-group--${modifier}`;
      group.setAttribute('aria-labelledby', `HeaderSearch${modifier}Heading`);
      const heading = document.createElement('h2');
      heading.id = `HeaderSearch${modifier}Heading`;
      heading.className = 'main-search-result__heading';
      heading.innerText = title;
      const grid = document.createElement('div');
      grid.className = 'product-grid product-grid--per-row-4 product-grid--per-row-mob-2';
      blocks.slice(0, groupLimit).forEach((block) => grid.appendChild(prepareProductBlock(block)));
      group.appendChild(heading);
      group.appendChild(grid);
      return group;
    };
    const makeFreeSwatchesEntry = () => {
      const entry = document.createElement('a');
      entry.className = 'main-search-result main-search-result--free-swatches';
      entry.href = '/collections/free-swatches';
      entry.innerHTML = '<span class="main-search-result__free-icon" aria-hidden="true">✦</span><span><strong>Free Swatches</strong><small>Browse all fabric samples</small></span>';
      return entry;
    };
    const renderResults = (lists, query, linkURL, sequence, headerStyleQuery) => {
      if (sequence !== this.requestSequence) return;
      const unique = new Map();
      lists.filter(Boolean).forEach((list) => {
        list.querySelectorAll('.product-block:not(.collection-block):not(.page-block)').forEach((block) => {
          const key = block.dataset.productId || block.querySelector('a')?.href;
          if (key && !unique.has(key)) unique.set(key, block);
        });
      });
      const products = [];
      const swatches = [];
      unique.forEach((block) => {
        const title = block.dataset.searchTitle || block.querySelector('.product-block__title')?.innerText || '';
        if (!headerStyleQuery && !CozyologyPredictiveSearch.matchesWordPrefixes(query, title) && !CozyologyPredictiveSearch.isSwatchIntent(query)) return;
        (block.dataset.searchKind === 'swatch' ? swatches : products).push(block);
      });
      const swatchIntent = CozyologyPredictiveSearch.isSwatchIntent(query);
      resultsBox.innerHTML = '';
      this.classList.remove('main-search--loading', 'main-search--no-results');
      setBusy('false');
      const groups = [];
      if (swatches.length || swatchIntent) {
        const swatchGroup = makeGroup('Swatches & Samples', 'swatches', swatchIntent ? swatches.slice(0, groupLimit - 1) : swatches);
        if (swatchIntent) swatchGroup.querySelector('.product-grid').prepend(makeFreeSwatchesEntry());
        groups.push(swatchGroup);
      }
      if (products.length) groups.push(makeGroup('Products', 'products', products));
      if (groups.length) {
        this.classList.add('main-search--has-results');
        groups.forEach((group) => resultsBox.appendChild(group));
        const allLink = document.createElement('a');
        allLink.className = 'main-search__results-all-link btn btn--secondary';
        allLink.href = linkURL;
        allLink.innerText = theme.strings.generalSearchViewAll;
        resultsBox.appendChild(allLink);
        setExpanded('true');
      } else {
        this.classList.remove('main-search--has-results');
        this.classList.add('main-search--no-results');
        const emptyMessage = document.createElement('div');
        emptyMessage.className = 'main-search__empty-message';
        emptyMessage.innerText = theme.strings.generalSearchNoResultsWithoutTerms;
        resultsBox.appendChild(emptyMessage);
        setExpanded('false');
      }
    };
    const renderFailure = (sequence, linkURL) => {
      if (sequence !== this.requestSequence) return;
      this.classList.remove('main-search--loading', 'main-search--has-results');
      this.classList.add('main-search--no-results');
      setBusy('false');
      resultsBox.innerHTML = '<div class="main-search__empty-message">Search suggestions are temporarily unavailable.</div>';
      const allLink = document.createElement('a');
      allLink.className = 'main-search__results-all-link btn btn--secondary';
      allLink.href = linkURL;
      allLink.innerText = theme.strings.generalSearchViewAll;
      resultsBox.appendChild(allLink);
      setExpanded('false');
    };
    const handleInputChange = () => {
      const rawValue = searchInput.value;
      const valueToSearch = CozyologyPredictiveSearch.normalize(rawValue);
      const headerStyleQuery = window.headerStyleSearch && window.headerStyleSearch.queryFor(rawValue);
      if (!valueToSearch) {
        this.oldSearchValue = '';
        this.requestSequence += 1;
        abortSearch();
        this.classList.remove('main-search--has-results', 'main-search--loading', 'main-search--no-results');
        setBusy('false');
        resultsBox.innerHTML = '';
        setExpanded('false');
        return;
      }
      if (valueToSearch === this.oldSearchValue) return;
      this.oldSearchValue = valueToSearch;
      abortSearch();
      const linkURL = buildLinkURL(headerStyleQuery);
      const sequence = ++this.requestSequence;
      this.classList.remove('main-search--has-results', 'main-search--no-results');
      this.classList.add('main-search--loading');
      setBusy('true');
      resultsBox.innerHTML = '<div class="main-search__results-spinner"><div class="loading-spinner"></div><span class="visually-hidden">Loading search suggestions</span></div>';
      searchTimeoutID = setTimeout(() => {
        const queries = [];
        if (!headerStyleQuery && !CozyologyPredictiveSearch.isSwatchIntent(valueToSearch)) queries.push(`${valueToSearch} booklet`);
        queries.push(headerStyleQuery || valueToSearch);
        searchAbortControllers = queries.map(() => new AbortController());
        Promise.allSettled(queries.map((query, index) => requestText(buildAjaxURL(query, headerStyleQuery, linkURL), searchAbortControllers[index]))).then((settled) => {
          if (sequence !== this.requestSequence) return;
          searchAbortControllers = [];
          const responses = settled.filter((result) => result.status === 'fulfilled').map((result) => result.value);
          if (responses.length) renderResults(responses.map(parseResponse), valueToSearch, linkURL, sequence, headerStyleQuery);
          else if (settled.some((result) => result.reason && result.reason.name !== 'AbortError')) renderFailure(sequence, linkURL);
        });
      }, searchTimeoutThrottle);
    };
    searchInput.addEventListener('input', handleInputChange.bind(this));
    searchInput.addEventListener('keyup', handleInputChange.bind(this));
    searchInput.addEventListener('change', handleInputChange.bind(this));
  }
};

window.customElements.define('main-search', MainSearch);
